import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Check, Phone, CheckCheck, Square } from 'lucide-react';
import { Button } from '../ui/Button.jsx';

/**
 * EndpointPickerDialog — 外撥分機選擇對話框
 *
 * 大畫面、響應式格狀勾選。selected 幾支 = 並發數（SDX 一支分機一通）。
 * 內部維護 draft，按「確定」才 commit；「取消」還原。
 *
 * props:
 *   open      bool
 *   onClose   () => void
 *   ipPhones  [{ ext, authUser? }]
 *   value     string[]            目前已選 ext
 *   onConfirm (exts: string[]) => void
 */
export function EndpointPickerDialog({ open, onClose, ipPhones = [], value = [], onConfirm }) {
    const { t } = useTranslation('outbound');
    const [draft, setDraft] = useState(value);

    // 每次打開以最新 value 初始化 draft
    useEffect(() => {
        if (open) setDraft(value);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    if (!open) return null;

    const total = ipPhones.length;
    const allSelected = draft.length === total && total > 0;

    const toggle = (ext) => setDraft(draft.includes(ext)
        ? draft.filter((e) => e !== ext)
        : [...draft, ext]);
    const toggleAll = () => setDraft(allSelected ? [] : ipPhones.map((p) => String(p.ext)));

    return (
        <div className="fixed inset-0 z-[60] bg-ink-900/50 backdrop-blur-sm grid place-items-center px-4">
            <div className="bg-cream-50 rounded-2xl shadow-[0_24px_70px_-20px_rgba(28,26,23,0.45)] w-full max-w-4xl max-h-[88vh] flex flex-col overflow-hidden">

                {/* Header */}
                <div className="flex items-start justify-between px-6 py-5 border-b border-cream-200 bg-white">
                    <div className="flex items-start gap-3">
                        <span className="size-10 inline-flex items-center justify-center rounded-xl bg-accent-50 text-accent-600 shrink-0">
                            <Phone size={18} />
                        </span>
                        <div>
                            <h3 className="heading-display text-lg font-semibold text-ink-900">{t('endpointPicker.title')}</h3>
                            <p className="text-[12px] text-ink-500 mt-0.5">{t('endpointPicker.desc')}</p>
                        </div>
                    </div>
                    <button onClick={onClose} className="size-9 inline-flex items-center justify-center text-ink-500 hover:bg-cream-100 rounded-md transition">
                        <X size={18} />
                    </button>
                </div>

                {/* Toolbar */}
                <div className="flex items-center justify-between px-6 py-3 border-b border-cream-200 bg-cream-50/60">
                    <div className="flex items-center gap-2 text-[13px]">
                        <span className="inline-flex items-center justify-center min-w-7 h-6 px-2 rounded-full bg-accent-500 text-white text-[12px] font-semibold tabular-nums">
                            {draft.length}
                        </span>
                        <span className="text-ink-600">{t('endpointPicker.selected', { n: draft.length, total })}</span>
                    </div>
                    <button
                        type="button"
                        onClick={toggleAll}
                        className="inline-flex items-center gap-1.5 text-[12px] font-medium text-accent-600 hover:text-accent-700"
                    >
                        {allSelected ? <Square size={14} /> : <CheckCheck size={14} />}
                        {allSelected ? t('wizard.s4.endpointsNone') : t('wizard.s4.endpointsAll')}
                    </button>
                </div>

                {/* Grid */}
                <div className="flex-1 overflow-y-auto px-6 py-5">
                    {total === 0 ? (
                        <div className="grid place-items-center h-40 text-[13px] text-ink-400">
                            {t('wizard.s4.endpointsEmpty')}
                        </div>
                    ) : (
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2.5">
                            {ipPhones.map((p) => {
                                const ext = String(p.ext);
                                const checked = draft.includes(ext);
                                return (
                                    <button
                                        key={ext}
                                        type="button"
                                        onClick={() => toggle(ext)}
                                        className={[
                                            'group flex items-center gap-2.5 px-3 py-2.5 rounded-xl border text-left transition',
                                            checked
                                                ? 'border-accent-400 bg-accent-50 shadow-[0_1px_0_rgba(28,26,23,0.02)]'
                                                : 'border-cream-300 bg-white hover:border-accent-300 hover:bg-cream-50'
                                        ].join(' ')}
                                    >
                                        <span className={[
                                            'size-5 rounded-md border flex items-center justify-center shrink-0 transition',
                                            checked ? 'bg-accent-500 border-accent-500 text-white' : 'border-cream-400 text-transparent'
                                        ].join(' ')}>
                                            <Check size={13} strokeWidth={3} />
                                        </span>
                                        <span className="min-w-0">
                                            <span className={['block font-mono text-[14px] leading-tight', checked ? 'text-ink-900' : 'text-ink-600'].join(' ')}>
                                                {ext}
                                            </span>
                                            {p.authUser && p.authUser !== ext && (
                                                <span className="block text-[10px] text-ink-400 truncate">{p.authUser}</span>
                                            )}
                                        </span>
                                    </button>
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="px-6 py-4 border-t border-cream-200 bg-white flex items-center justify-between">
                    <div className="text-[12px] text-ink-400">
                        {t('wizard.s4.endpointsHint', { n: draft.length || total })}
                    </div>
                    <div className="flex items-center gap-2">
                        <Button variant="ghost" onClick={onClose}>{t('endpointPicker.cancel')}</Button>
                        <Button variant="accent" onClick={() => { onConfirm(draft); onClose(); }}>{t('endpointPicker.confirm')}</Button>
                    </div>
                </div>
            </div>
        </div>
    );
}
